import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Cell, Legend
} from "recharts";
import { useAppearance } from "../../contexts/AppearanceContext";
import { TrendingUp, Target, Clock, CheckSquare, DollarSign, Users, Trophy, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#f97316"];

function fmtK(v) {
  if (!v) return "R$ 0";
  if (v >= 1000000) return `R$ ${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `R$ ${(v/1000).toFixed(0)}k`;
  return `R$ ${Math.round(v)}`;
}
function fmt(v) { return new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL",maximumFractionDigits:0}).format(v||0); }

function KpiBox({ title, value, sub, icon: Icon, color, tc500 }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color || tc500}18` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: color || tc500 }} />
        </div>
      </div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function PerformanceReport({ filterHook }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        pipeline_ids: filterHook.filters.pipelineIds,
        responsavel_ids: filterHook.filters.responsavelIds,
        period: {
          start: filterHook.filters.periodStart + "T00:00:00Z",
          end: filterHook.filters.periodEnd + "T23:59:59Z",
          field: "ganho_em",
        },
      };
      const res = await axios.post(`${API}/reports/performance`, { filters }, { withCredentials: true });
      setData(res.data);
      if (res.data.users?.length > 0) setSelectedUser(res.data.users[0].user_id);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterHook.filters, filterHook.buildApiFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-48 text-slate-400 text-sm">{t("common.loading") || "Carregando..."}</div>;
  if (!data || !data.users?.length) return (
    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-slate-200 text-slate-400">
      <Users className="w-10 h-10 mb-3 text-slate-300" />
      <p className="text-sm">{t("relatorios.noPerformance")}</p>
    </div>
  );

  const { users, totals, all_months } = data;
  const selected = users.find(u => u.user_id === selectedUser) || users[0];

  // Radar data for selected user (normalized 0-100)
  const maxMrr = Math.max(...users.map(u => u.mrr_ganho), 1);
  const maxDeals = Math.max(...users.map(u => u.deals_ganhos), 1);
  const maxTasks = Math.max(...users.map(u => u.tarefas_concluidas), 1);
  const maxPipeline = Math.max(...users.map(u => u.deals_abertos), 1);

  const radarData = users.map(u => ({
    name: u.name.split(" ")[0],
    MRR:      Math.round(u.mrr_ganho / maxMrr * 100),
    Deals:    Math.round(u.deals_ganhos / maxDeals * 100),
    Conv:     Math.round(u.taxa_conversao),
    Tasks:    Math.round(u.tarefas_concluidas / maxTasks * 100),
    Pipeline: Math.round(u.deals_abertos / maxPipeline * 100),
  }));

  // Monthly evolution
  const monthlyData = all_months.map(month => {
    const row = { period: month };
    users.forEach(u => {
      const m = u.monthly.find(x => x.period === month);
      row[u.name.split(" ")[0]] = m ? m.mrr : 0;
    });
    return row;
  });

  return (
    <div className="space-y-6">
      {/* Team KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <KpiBox title={t("relatorios.kpiMrrGanho")} value={fmtK(totals.mrr_ganho)} sub={`${totals.deals_ganhos} ${t("relatorios.subDeals")}`} icon={DollarSign} tc500={tc500} />
        <KpiBox title="TCV" value={fmtK(totals.tcv_ganho)} sub="MRR×12 + Setup" icon={TrendingUp} tc500={tc500} />
        <KpiBox title={t("relatorios.kpiConversao")} value={`${totals.taxa_conversao}%`} sub={t("relatorios.subMediaEquipe")} icon={Target} tc500={tc500} />
        <KpiBox title={t("relatorios.kpiTicketMedio")} value={fmtK(totals.ticket_medio_mrr)} sub={t("relatorios.subMrrDeal")} icon={DollarSign} tc500={tc500} />
        <KpiBox title={t("relatorios.kpiTMedio")} value={`${totals.tempo_medio_dias}d`} sub={t("relatorios.subCriacaoGanho")} icon={Clock} tc500={tc500} />
        <KpiBox title={t("relatorios.kpiAtividades")} value={totals.tarefas_concluidas} sub={t("relatorios.subTarefasConcluidas")} icon={CheckSquare} tc500={tc500} />
        <KpiBox title={t("relatorios.kpiVendedores")} value={users.length} sub={t("relatorios.subComDeals")} icon={Users} tc500={tc500} />
      </div>

      {/* Ranking + Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ranking by MRR */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.rankingMrr")}
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(users.length * 44, 120)}>
            <BarChart data={users.map(u => ({ name: u.name.split(" ")[0], mrr: u.mrr_ganho, color: COLORS[users.indexOf(u) % COLORS.length] }))}
              layout="vertical" margin={{ top: 0, right: 60, left: 70, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={66} />
              <RT formatter={v => [fmt(v), t("relatorios.colMrr")]} contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="mrr" radius={[0, 4, 4, 0]}>
                {users.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Radar Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <Target className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.comparativo360")}
            <span className="text-[10px] text-slate-400 font-normal">{t("relatorios.normalized")}</span>
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={[
              { metric: t("relatorios.colMrr"),       ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.mrr_ganho/maxMrr*100)])) },
              { metric: t("relatorios.colDeals"),     ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.deals_ganhos/maxDeals*100)])) },
              { metric: t("relatorios.kpiConversao"), ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.taxa_conversao)])) },
              { metric: t("relatorios.kpiAtividades"),...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.tarefas_concluidas/maxTasks*100)])) },
              { metric: t("relatorios.colPipeline"),  ...Object.fromEntries(users.map(u => [u.name.split(" ")[0], Math.round(u.deals_abertos/maxPipeline*100)])) },
            ]}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: "#64748b" }} />
              {users.slice(0, 5).map((u, i) => (
                <Radar key={u.user_id} name={u.name.split(" ")[0]} dataKey={u.name.split(" ")[0]}
                  stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.1} strokeWidth={2} />
              ))}
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <RT contentStyle={{ fontSize: 12 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Evolution */}
      {all_months.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: tc500 }} />{t("relatorios.mrrEvolution")}
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={monthlyData} margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
              <RT formatter={v => [fmt(v), t("relatorios.colMrr")]} contentStyle={{ fontSize: 12 }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              {users.slice(0, 5).map((u, i) => (
                <Line key={u.user_id} type="monotone" dataKey={u.name.split(" ")[0]}
                  stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Detailed Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Briefcase className="w-4 h-4" style={{ color: tc500 }} />
          <h3 className="text-sm font-semibold text-slate-700">{t("relatorios.detailByVendedorFull")}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {[t("relatorios.colVendedor"),t("relatorios.mrrGanho"),t("relatorios.colTcv"),t("relatorios.kpiTicketMedio"),t("relatorios.colDealsGanhos"),t("relatorios.colDealsPerdidos"),t("relatorios.kpiConversao"),t("relatorios.kpiTMedio"),t("relatorios.colPipelineAtivo"),t("relatorios.kpiAtividades")].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                        {i + 1}
                      </div>
                      <span className="font-medium text-slate-800 truncate max-w-[130px]">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold" style={{ color: tc500 }}>{fmtK(u.mrr_ganho)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtK(u.tcv_ganho)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtK(u.ticket_medio_mrr)}</td>
                  <td className="px-4 py-3 text-emerald-600 font-medium">{u.deals_ganhos}</td>
                  <td className="px-4 py-3 text-rose-500">{u.deals_perdidos}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full" style={{ width: `${u.taxa_conversao}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700">{u.taxa_conversao}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.tempo_medio_dias > 0 ? `${u.tempo_medio_dias}d` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.deals_abertos} <span className="text-xs text-slate-400">({fmtK(u.mrr_aberto)})</span></td>
                  <td className="px-4 py-3 text-slate-600">{u.tarefas_concluidas}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
